import { DevToolsMode } from '@fullstory/client-api';
import { PLAYBACK_EMBED_NETWORK_SIDECAR_ROUTE, PLAYBACK_NETWORK_SIDECAR_ROUTE } from '.';
import {
  PLAYBACK_EMBED_ROUTE,
  PLAYBACK_EMBED_DEV_TOOLS_ROUTE,
  PLAYBACK_DEV_TOOLS_ROUTE,
  PLAYBACK_ROUTE,
} from './routes';
import { generateRoutePath, matchRoutePath } from './utils';

export const DEVTOOLS_PATH = 'devtools';

export const CONSOLE_PATH = 'console';
export const NETWORK_PATH = 'network';
export type DevToolsModeParam = typeof CONSOLE_PATH | typeof NETWORK_PATH | '';

// TODO(jordanhurt): Register this param on the appropriate playback routes via .queryParam
export const AUTO_SELECT_QUERY_PARAM = 'autoselect';
export const AUTO_OPEN_NOTE_MODAL = 'openNote';

const convertModePathToEnum = (modePath?: DevToolsModeParam): DevToolsMode => {
  switch (modePath) {
    case CONSOLE_PATH:
      return DevToolsMode.Console;
    case NETWORK_PATH:
      return DevToolsMode.Network;
    default:
      return DevToolsMode.None;
  }
};

const convertModeEnumToPath = (mode?: DevToolsMode | undefined): DevToolsModeParam => {
  switch (mode) {
    case DevToolsMode.Console:
      return CONSOLE_PATH;
    case DevToolsMode.Network:
      return NETWORK_PATH;
    default:
      return '';
  }
};

const maybeDecodePathname = (pathname: string): string => {
  try {
    return decodeURIComponent(pathname);
  } catch (e) {
    return pathname;
  }
};

type URLLike = Pick<URL, 'pathname' | 'search'>;

/**
 * Generates either the standard or embed playback route based on the current location
 */
export const generatePlaybackRoute = (url: URLLike): string | undefined => {
  const { pathname, search } = url;
  const decodedPathname = maybeDecodePathname(pathname);
  const embedMatch =
    matchRoutePath(decodedPathname, PLAYBACK_EMBED_DEV_TOOLS_ROUTE) ||
    matchRoutePath(decodedPathname, PLAYBACK_EMBED_ROUTE);
  const playbackMatch =
    matchRoutePath(decodedPathname, PLAYBACK_DEV_TOOLS_ROUTE) ||
    matchRoutePath(decodedPathname, PLAYBACK_ROUTE);

  if (playbackMatch) {
    return generateRoutePath(PLAYBACK_ROUTE, playbackMatch.params, null, search);
  } else if (embedMatch) {
    return generateRoutePath(PLAYBACK_EMBED_ROUTE, embedMatch.params, null, search);
  }

  return undefined;
};

/**
 * Generates either the standard or embed playback devtools route based on the current location
 */
export const generatePlaybackDevToolsRoute = (
  url: URLLike,
  mode: DevToolsMode,
): string | undefined => {
  const { pathname, search } = url;
  const decodedPathname = maybeDecodePathname(pathname);
  const embedMatch = matchRoutePath(decodedPathname, PLAYBACK_EMBED_ROUTE);
  const playbackMatch = matchRoutePath(decodedPathname, PLAYBACK_ROUTE);

  if (playbackMatch) {
    return generateRoutePath(
      PLAYBACK_DEV_TOOLS_ROUTE,
      {
        ...playbackMatch.params,
        mode: convertModeEnumToPath(mode),
      },
      null,
      search,
    );
  } else if (embedMatch) {
    return generateRoutePath(
      PLAYBACK_EMBED_DEV_TOOLS_ROUTE,
      {
        ...embedMatch.params,
        mode: convertModeEnumToPath(mode),
      },
      null,
      search,
    );
  }

  return undefined;
};

export const generatePlaybackNetworkSidecarRoute = (
  url: URLLike,
  pageIdx: number,
  entryIdx: number,
): string | undefined => {
  const { pathname, search } = url;
  const decodedPathname = maybeDecodePathname(pathname);
  const embedMatch = matchRoutePath(decodedPathname, PLAYBACK_EMBED_ROUTE);
  const playbackMatch = matchRoutePath(decodedPathname, PLAYBACK_ROUTE);

  if (playbackMatch) {
    return generateRoutePath(
      PLAYBACK_NETWORK_SIDECAR_ROUTE,
      {
        ...playbackMatch.params,
        pageIdx: pageIdx.toString(),
        entryIdx: entryIdx.toString(),
      },
      null,
      search,
    );
  } else if (embedMatch) {
    return generateRoutePath(
      PLAYBACK_EMBED_NETWORK_SIDECAR_ROUTE,
      {
        ...embedMatch.params,
        pageIdx: pageIdx.toString(),
        entryIdx: entryIdx.toString(),
      },
      null,
      search,
    );
  }

  return undefined;
};

const getCurrentPathname = (): string => {
  const { pathname } = new URL(window?.location.toString());
  return pathname;
};

/**
 * Tries to match the standard or embed playback base route
 */
export const matchPlaybackRoutePath = (pathname: string = getCurrentPathname()) =>
  matchRoutePath(pathname, PLAYBACK_ROUTE) || matchRoutePath(pathname, PLAYBACK_EMBED_ROUTE);

/**
 * Tries to match the standard or embed playback devtools route
 */
export const matchPlaybackDevToolsRoutePath = (pathname: string = getCurrentPathname()) =>
  matchRoutePath(pathname, PLAYBACK_DEV_TOOLS_ROUTE) ||
  matchRoutePath(pathname, PLAYBACK_EMBED_DEV_TOOLS_ROUTE);

/**
 * Returns true if the current path matches the standard or embed playback devtools route
 */
export const isPlaybackDevToolsRoutePath = (pathname: string = getCurrentPathname()): boolean =>
  !!matchPlaybackDevToolsRoutePath(pathname);

/**
 * Tries to match the standard or embed playback network sidecar route
 */
export const matchPlaybackNetworkSidecarRoutePath = (pathname: string = getCurrentPathname()) =>
  matchRoutePath(pathname, PLAYBACK_EMBED_NETWORK_SIDECAR_ROUTE) ||
  matchRoutePath(pathname, PLAYBACK_NETWORK_SIDECAR_ROUTE);

/**
 * Returns true if the current path matches the standard or embed playback network sidecar route
 */
export const isPlaybackNetworkSidecarRoutePath = (
  pathname: string = getCurrentPathname(),
): boolean => !!matchPlaybackNetworkSidecarRoutePath(pathname);

/**
 * Gets the current devtools mode from the pathname or returns "none" if the path is not a devtools route
 */
export const getPlaybackDevToolsMode = (pathname: string = getCurrentPathname()): DevToolsMode => {
  const matchedRoute = matchPlaybackDevToolsRoutePath(pathname);

  return convertModePathToEnum(matchedRoute?.params.mode);
};
